import { DateCalendar } from "@mui/x-date-pickers";
import moment from "moment";
import { useEffect, useState } from "react";
import { Button } from "../buttons/button";
import { PopoverMenu } from "../dropdown/popover";
import { Icon } from "../icon/icon";

export type DateRangePreset =
  | "this-week"
  | "last-30-days"
  | "last-90-days"
  | "last-year"
  | "custom";

export type DateRangeValue = {
  preset?: DateRangePreset;
  startDate?: string;
  endDate?: string;
};

const presets: { label: string; key: DateRangePreset }[] = [
  { label: "This Week", key: "this-week" },
  { label: "Last 30 Days", key: "last-30-days" },
  { label: "Last 90 Days", key: "last-90-days" },
  { label: "Last 1 Year", key: "last-year" },
  { label: "Custom Date", key: "custom" },
];

const getPresetRange = (
  key: Exclude<DateRangePreset, "custom">,
): { startDate: string; endDate: string } => {
  const end = moment();
  let start: moment.Moment;

  switch (key) {
    case "this-week":
      start = moment().startOf("week");
      break;
    case "last-30-days":
      start = moment().subtract(30, "days");
      break;
    case "last-90-days":
      start = moment().subtract(90, "days");
      break;
    case "last-year":
      start = moment().subtract(1, "year");
      break;
  }

  return {
    startDate: start.format("YYYY-MM-DD"),
    endDate: end.format("YYYY-MM-DD"),
  };
};

/**
 * Formats a date range for display.
 * Same year: "Jun 19 - Jun 20, 2026".
 * Different years: "Jun 19, 2026 - Jan 5, 2027".
 */
export const formatDateRange = (start?: string, end?: string): string => {
  if (!start || !end) return "";
  const s = moment(start);
  const e = moment(end);

  if (s.year() === e.year()) {
    return `${s.format("MMM D")} - ${e.format("MMM D, YYYY")}`;
  }
  return `${s.format("MMM D, YYYY")} - ${e.format("MMM D, YYYY")}`;
};

export const DateRangeFilter = ({
  value,
  onChange,
  className = "",
}: {
  value?: DateRangeValue;
  onChange: (range: DateRangeValue) => void;
  className?: string;
}) => {
  const [view, setView] = useState<
    "default" | "custom" | "start-picker" | "end-picker"
  >("default");
  const [error, setError] = useState<string | null>(null);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [startDate, setStartDate] = useState<moment.Moment | null>(
    value?.startDate ? moment(value.startDate) : null,
  );
  const [endDate, setEndDate] = useState<moment.Moment | null>(
    value?.endDate ? moment(value.endDate) : null,
  );

  const isActive = Boolean(value?.startDate && value?.endDate);

  useEffect(() => {
    if (startDate && endDate && !startDate.isAfter(endDate)) {
      setError(null);
    }
  }, [startDate, endDate]);

  useEffect(() => {
    if (!isOpen) {
      setView("default");
    }
  }, [isOpen]);

  const handlePresetClick = (key: DateRangePreset, close: () => void) => {
    if (key === "custom") {
      setStartDate(value?.startDate ? moment(value.startDate) : null);
      setEndDate(value?.endDate ? moment(value.endDate) : null);
      setView("custom");
      return;
    }

    onChange({ preset: key, ...getPresetRange(key) });
    close();
    setView("default");
  };

  const calendarSx = {
    height: "245px",
    width: "100%",
    maxWidth: "100%",
    "& .MuiPickersCalendarHeader-root": { display: "none" },
    "& .MuiDayCalendar-weekContainer": {
      marginBottom: 0,
      justifyContent: "space-between",
    },
    "& .MuiDayCalendar-weekDayLabel": {
      fontFamily: "Fustat, sans-serif",
      fontWeight: 500,
      width: "100%",
      fontSize: "14px",
      color: "#7081A0",
      marginTop: "8px",
    },
    "& .MuiPickersDay-root": {
      fontFamily: "Fustat, sans-serif",
      fontWeight: 600,
      fontSize: "14px",
      color: "#091525",
    },
    "& .MuiPickersDay-dayOutsideMonth, & .MuiPickersDay-root.Mui-disabled": {
      color: "#A9BBD1 !important",
      fontWeight: 600,
      fontSize: "14px",
    },
    "& .MuiPickersDay-root.Mui-selected": {
      backgroundColor: "#E7ECF2 !important",
      color: "#091525 !important",
      borderRadius: "8px",
      border: "transparent",
    },
    "& .MuiPickersDay-today": {
      borderRadius: "100%",
    },
  };

  return (
    <PopoverMenu
      className={`min-w-max ${className}`}
      position="bottom end"
      menuClassName="!min-w-fit !z-10 !overflow-visible !shadow-none"
      customMenuItems={({ close }) => {
        return (
          <div
            className={`flex flex-col overflow-hidden border border-grey-100 bg-white shadow-sm ${
              view === "default"
                ? "w-[239px] rounded-[8px]"
                : view === "custom"
                  ? "w-[300px] rounded-[12px]"
                  : "w-[328px] rounded-[12px]"
            }`}
          >
            {view === "default" && (
              <>
                {presets.map((preset) => (
                  <button
                    key={preset.key}
                    type="button"
                    onClick={() => handlePresetClick(preset.key, close)}
                    className="flex min-h-[52px] w-full items-center border-b border-b-grey-100 px-4 text-left text-sm last:border-b-0"
                  >
                    <span className="font-bold text-grey-950">
                      {preset.label}
                    </span>
                  </button>
                ))}
              </>
            )}

            {view === "custom" && (
              <div className="flex flex-col gap-3 p-4">
                <div className="flex items-center gap-2">
                  <Icon
                    svg="arrow-left-two"
                    width={24}
                    height={24}
                    className="cursor-pointer"
                    onClick={() => setView("default")}
                  />
                  <p className="text-base font-bold text-dark-blue-500">
                    Custom Range
                  </p>
                </div>
                <div className="flex flex-col gap-3">
                  <main className="flex flex-col gap-1">
                    <button
                      type="button"
                      onClick={() => setView("start-picker")}
                      className={`flex w-full items-center justify-between rounded-[8px] border bg-grey-50 px-2.5 py-2 ${error ? "border-danger" : "border-grey-100"}`}
                    >
                      <div className="flex flex-col gap-1">
                        <p className="text-left text-[11px] text-grey-700">
                          Start Date
                        </p>
                        <span
                          className={`text-sm leading-[14px] ${
                            startDate
                              ? "font-bold text-grey-950"
                              : "text-grey-500"
                          } `}
                        >
                          {startDate
                            ? startDate.format("MMM D, YYYY")
                            : "DD/MM/YYYY"}
                        </span>
                      </div>
                      <Icon
                        svg="calendar-2"
                        width={24}
                        height={24}
                        className="text-grey-800"
                      />
                    </button>
                    {error && (
                      <p className="text-xs font-medium text-danger">{error}</p>
                    )}
                  </main>

                  <button
                    type="button"
                    onClick={() => setView("end-picker")}
                    className="flex w-full items-center justify-between rounded-[8px] border border-grey-100 bg-grey-50 px-2.5 py-2"
                  >
                    <div className="flex flex-col gap-1">
                      <p className="text-left text-[11px] text-grey-700">
                        End Date
                      </p>
                      <span
                        className={`text-sm leading-[14px] ${
                          endDate ? "font-bold text-grey-950" : "text-grey-500"
                        } `}
                      >
                        {endDate ? endDate.format("MMM D, YYYY") : "DD/MM/YYYY"}
                      </span>
                    </div>
                    <Icon
                      svg="calendar-2"
                      width={24}
                      height={24}
                      className="text-grey-800"
                    />
                  </button>
                </div>
                <Button
                  type="button"
                  variation="primary"
                  text="Apply"
                  disabled={!startDate || !endDate}
                  onClick={() => {
                    if (!startDate || !endDate) return;

                    if (startDate.isAfter(endDate)) {
                      setError("Start date cannot be later than end date");
                      return;
                    }
                    setError(null);
                    onChange({
                      preset: "custom",
                      startDate: startDate.format("YYYY-MM-DD"),
                      endDate: endDate.format("YYYY-MM-DD"),
                    });
                    close();
                    setView("default");
                  }}
                />
              </div>
            )}

            {view === "start-picker" && (
              <div className="flex flex-col">
                <div className="flex items-center border-y border-grey-200 p-3">
                  <Icon
                    svg="caret-down"
                    width={20}
                    height={20}
                    className="rotate-90 cursor-pointer text-grey-500"
                    onClick={() =>
                      setStartDate((prev) =>
                        moment(prev || moment()).subtract(1, "month"),
                      )
                    }
                  />
                  <p className="flex-1 text-center font-[Fustat] text-base font-bold text-primary">
                    {moment(startDate || moment()).format("MMMM YYYY")}
                  </p>
                  <Icon
                    svg="caret-down"
                    width={20}
                    height={20}
                    className="-rotate-90 cursor-pointer text-grey-500"
                    onClick={() =>
                      setStartDate((prev) =>
                        moment(prev || moment()).add(1, "month"),
                      )
                    }
                  />
                </div>

                <DateCalendar
                  value={startDate || moment()}
                  onChange={(newDate) => {
                    if (newDate) {
                      setStartDate(moment(newDate));
                      setView("custom");
                    }
                  }}
                  views={["day"]}
                  showDaysOutsideCurrentMonth
                  reduceAnimations
                  maxDate={moment()}
                  dayOfWeekFormatter={(day: any) =>
                    String(day).charAt(0).toUpperCase() +
                    String(day).charAt(1).toLowerCase()
                  }
                  sx={calendarSx}
                />
              </div>
            )}

            {view === "end-picker" && (
              <div className="flex flex-col">
                <div className="flex items-center border-y border-grey-200 p-2">
                  <Icon
                    svg="caret-down"
                    width={20}
                    height={20}
                    className="rotate-90 cursor-pointer text-grey-500"
                    onClick={() =>
                      setEndDate((prev) =>
                        moment(prev || moment()).subtract(1, "month"),
                      )
                    }
                  />
                  <p className="flex-1 text-center font-[Fustat] text-base font-bold text-primary">
                    {moment(endDate || moment()).format("MMMM YYYY")}
                  </p>
                  <Icon
                    svg="caret-down"
                    width={20}
                    height={20}
                    className="-rotate-90 cursor-pointer text-grey-500"
                    onClick={() =>
                      setEndDate((prev) =>
                        moment(prev || moment()).add(1, "month"),
                      )
                    }
                  />
                </div>

                <DateCalendar
                  value={endDate || moment()}
                  minDate={startDate || undefined}
                  onChange={(newDate) => {
                    if (newDate) {
                      setEndDate(moment(newDate));
                      setView("custom");
                    }
                  }}
                  views={["day"]}
                  showDaysOutsideCurrentMonth
                  reduceAnimations
                  maxDate={moment()}
                  dayOfWeekFormatter={(day: any) =>
                    String(day).charAt(0).toUpperCase() +
                    String(day).charAt(1).toLowerCase()
                  }
                  sx={calendarSx}
                />
              </div>
            )}
          </div>
        );
      }}
    >
      {({ open }) => {
        if (open !== isOpen) {
          setIsOpen(open);
        }
        return (
          <Button
            type="button"
            text="Date Filter"
            icon="calendar-2"
            iconDimension={20}
            variation="grey"
            className={`!min-w-fit !rounded-2xl !p-3 !text-sm ${open || isActive ? "!bg-grey-100" : ""}`}
          />
        );
      }}
    </PopoverMenu>
  );
};
